import React, { useState, useEffect } from 'react';
import AdminLayoutWrapper from '../../components/layout/AdminLayoutWrapper';
import DataTableLayout from '../../components/common/DataTableLayout';
import FilterPanel from '../../components/common/FilterPanel';
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Tooltip,
  Chip,
  Alert,
  Snackbar
} from '@mui/material';
import { Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { DataGrid } from '@mui/x-data-grid';
import {
  GridToolbarColumnsButton,
  GridToolbarDensitySelector,
  GridToolbarExport,
} from '@mui/x-data-grid';
import { DIFFICULTIES } from '../../config/Config';
import { useNavigate } from 'react-router-dom';
import { createFilterChangeHandler, clearFilters, filterData, dataGridProps } from '../../utils/tableUtils';
import api from '../../services/api';

export default function DifficultyManagementPage() {
  const [difficulties, setDifficulties] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({ name: '' });
  const [mobileFilterOpen, setMobileFilterOpen] = useState(false);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: '' });
  const [editMode, setEditMode] = useState(false);
  const [currentId, setCurrentId] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const fetchPaginated = async (endpoint) => {
    let url = endpoint;
    let all = [];
    while (url) {
      const res = await api.get(url);
      const data = res.data;
      if (Array.isArray(data)) {
        all = data;
        break;
      }
      all = all.concat(data.results || []);
      url = data.next || null;
    }
    return all;
  };

  const fetchAll = async () => {
    setLoading(true);
    try {
      const allDifficulties = await fetchPaginated(DIFFICULTIES);
      setDifficulties(allDifficulties);
    } catch (err) {
      console.error('Error fetching difficulties:', err);
      showSnackbar('Failed to load difficulties', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAll();
  }, []);

  const handleOpen = (item = null) => {
    if (item) {
      setForm({ name: item.name });
      setEditMode(true);
      setCurrentId(item.id);
    } else {
      setForm({ name: '' });
      setEditMode(false);
      setCurrentId(null);
    }
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setForm({ name: '' });
    setEditMode(false);
    setCurrentId(null);
  };

  const handleSubmit = async () => {
    if (!form.name) {
      showSnackbar('Please enter a difficulty name', 'error');
      return;
    }

    try {
      if (editMode) {
        await api.put(`${DIFFICULTIES}${currentId}/`, form);
        showSnackbar('Difficulty updated successfully');
      } else {
        await api.post(DIFFICULTIES, form);
        showSnackbar('Difficulty created successfully');
      }
      handleClose();
      fetchAll();
    } catch (err) {
      console.error('Error saving difficulty:', err);
      showSnackbar('Failed to save difficulty', 'error');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this difficulty?')) return;
    try {
      await api.delete(`${DIFFICULTIES}${id}/`);
      showSnackbar('Difficulty deleted successfully');
      fetchAll();
    } catch (err) {
      console.error('Error deleting difficulty:', err);
      showSnackbar('Failed to delete difficulty', 'error');
    }
  };

  const filteredDifficulties = filterData(difficulties, filters);

  const handleFilterChange = createFilterChangeHandler(setFilters);
  const handleClearFilters = () => clearFilters(setFilters, { name: '' });

  const filterConfig = [
    {
      field: 'name',
      label: 'Name',
      type: 'text',
      placeholder: 'Search by name...'
    }
  ];

  const columns = [
    {
      field: 'name',
      headerName: 'Difficulty Name',
      flex: 2,
      renderCell: (params) => (
        <Chip 
          label={params.value} 
          size="small" 
          color="primary" 
          variant="outlined"
        />
      )
    },
    {
      field: 'created_at',
      headerName: 'Created',
      flex: 1,
      valueGetter: (params) => params.value ? new Date(params.value).toLocaleDateString() : 'N/A'
    },
    {
      field: 'actions',
      headerName: 'Actions',
      flex: 1,
      sortable: false,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Tooltip title="Edit">
            <IconButton
              size="small"
              onClick={() => handleOpen(params.row)}
              sx={{ p: 0.5 }}
            >
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete">
            <IconButton
              size="small"
              onClick={() => handleDelete(params.row.id)}
              sx={{ p: 0.5, color: 'error.main' }}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      )
    }
  ];

  const actionButtons = [
    {
      label: 'Add Difficulty',
      icon: <AddIcon />,
      variant: 'contained',
      onClick: () => handleOpen(null)
    }
  ];

  const emptyStateProps = {
    title: 'No difficulties found',
    description: 'Try adjusting your filters or add your first difficulty.',
    icon: <AddIcon sx={{ fontSize: 40, color: 'text.secondary' }} />
  };

  return (
    <AdminLayoutWrapper>
      <DataTableLayout
        title="Question Difficulties"
        actionButtons={actionButtons}
        filterPanel={
          <FilterPanel
            filters={filters}
            onFilterChange={handleFilterChange}
            onClearFilters={handleClearFilters}
            filterConfig={filterConfig}
          />
        }
        loading={loading}
        isEmpty={!loading && filteredDifficulties.length === 0}
        emptyStateProps={emptyStateProps}
        mobileFilterOpen={mobileFilterOpen}
        onMobileFilterClose={() => setMobileFilterOpen(!mobileFilterOpen)}
      >
        <DataGrid
          rows={filteredDifficulties}
          columns={columns}
          {...dataGridProps}
          components={{
            Toolbar: () => (
              <Box sx={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center',
                mb: 2,
                p: 1
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <GridToolbarColumnsButton 
                    sx={{ 
                      color: 'primary.main',
                      '&:hover': { backgroundColor: 'primary.50' }
                    }}
                  />
                  <GridToolbarDensitySelector 
                    sx={{ 
                      color: 'primary.main',
                      '&:hover': { backgroundColor: 'primary.50' }
                    }}
                  />
                  <GridToolbarExport 
                    sx={{ 
                      color: 'primary.main',
                      '&:hover': { backgroundColor: 'primary.50' }
                    }}
                  />
                </Box>
              </Box>
            )
          }}
        />
      </DataTableLayout>

      {/* Add/Edit Dialog */}
      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editMode ? 'Edit Question Difficulty' : 'Add Question Difficulty'}
        </DialogTitle>
        <DialogContent>
          <TextField
            label="Difficulty Name"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            fullWidth
            margin="normal"
            required
            autoFocus
            placeholder="e.g., Easy, Medium, Hard"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained">
            {editMode ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </AdminLayoutWrapper>
  );
}
